import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
  SelectChangeEvent,
} from '@mui/material';
import { PersonAdd, Save, CheckCircle } from '@mui/icons-material';
import { groupService } from '../services/groupService';
import { licenseService } from '../services/licenseService';
import { userService } from '../services/userService';
import { getApiCollection, getApiData, getProp } from '../utils/apiResponse';
import { styles } from '../utils/styles';

const steps = ['User Information', 'License Assignment', 'Group Membership', 'Review & Create'];

export const Onboarding: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Available options
  const [availableGroups, setAvailableGroups] = useState<any[]>([]);
  const [availableLicenses, setAvailableLicenses] = useState<any[]>([]);

  // Form data
  const [formData, setFormData] = useState({
    displayName: '',
    givenName: '',
    surname: '',
    userPrincipalName: '',
    mail: '',
    jobTitle: '',
    department: '',
    mobilePhone: '',
    officeLocation: '',
    selectedLicenses: [] as string[],
    selectedGroups: [] as string[],
  });

  useEffect(() => {
    loadGroupsAndLicenses();
  }, []);

  const loadGroupsAndLicenses = async () => {
    try {
      setError(null);
      const [groupsResponse, licensesResponse] = await Promise.all([
        groupService.getGroups(),
        licenseService.getLicenses(),
      ]);

      const groups = getApiCollection<any>(groupsResponse, 'loading onboarding groups');
      const licenses = getApiCollection<any>(licensesResponse, 'loading onboarding licenses');

      setAvailableGroups(groups);
      setAvailableLicenses(licenses);
    } catch (err: any) {
      setError(err.message || 'Failed to load groups and licenses');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (e: SelectChangeEvent<string[]>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      handleSubmit();
    } else {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      // Create user
      const newUser = {
        displayName: formData.displayName,
        givenName: formData.givenName,
        surname: formData.surname,
        userPrincipalName: formData.userPrincipalName,
        mailNickname: formData.userPrincipalName.split('@')[0],
        accountEnabled: true,
        passwordProfile: {
          forceChangePasswordNextSignIn: true,
          password: 'TempPassword123!', // Should be generated securely
        },
        jobTitle: formData.jobTitle,
        department: formData.department,
        mobilePhone: formData.mobilePhone,
        officeLocation: formData.officeLocation,
      };

      const userResponse = await userService.createUser(newUser);
      const createdUser = getApiData<any>(userResponse, 'creating user');
      const userId = getProp(createdUser, 'id');
      if (!userId) {
        throw new Error('User was created but no user id was returned.');
      }

      // TODO: Assign licenses (requires implementation)
      // TODO: Add to groups (requires implementation)

      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Display Name"
                name="displayName"
                value={formData.displayName}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="User Principal Name"
                name="userPrincipalName"
                value={formData.userPrincipalName}
                onChange={handleInputChange}
                placeholder="user@domain.com"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="First Name"
                name="givenName"
                value={formData.givenName}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="surname"
                value={formData.surname}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                name="mail"
                type="email"
                value={formData.mail}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Job Title"
                name="jobTitle"
                value={formData.jobTitle}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Department"
                name="department"
                value={formData.department}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Mobile Phone"
                name="mobilePhone"
                value={formData.mobilePhone}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Office Location"
                name="officeLocation"
                value={formData.officeLocation}
                onChange={handleInputChange}
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <FormControl fullWidth>
            <InputLabel>Select Licenses</InputLabel>
            <Select
              multiple
              name="selectedLicenses"
              value={formData.selectedLicenses}
              onChange={handleSelectChange}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const license = availableLicenses.find((l) => getProp(l, 'skuId') === value);
                    return <Chip key={value} label={getProp(license, 'skuPartNumber') || value} />;
                  })}
                </Box>
              )}
            >
              {availableLicenses.map((license) => (
                <MenuItem key={getProp(license, 'skuId')} value={getProp(license, 'skuId')}>
                  {getProp(license, 'skuPartNumber')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 2:
        return (
          <FormControl fullWidth>
            <InputLabel>Select Groups</InputLabel>
            <Select
              multiple
              name="selectedGroups"
              value={formData.selectedGroups}
              onChange={handleSelectChange}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const group = availableGroups.find((g) => getProp(g, 'id') === value);
                    return <Chip key={value} label={getProp(group, 'displayName') || value} />;
                  })}
                </Box>
              )}
            >
              {availableGroups.map((group) => (
                <MenuItem key={getProp(group, 'id')} value={getProp(group, 'id')}>
                  {getProp(group, 'displayName')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 3:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    User Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography>
                    <strong>Display Name:</strong> {formData.displayName}
                  </Typography>
                  <Typography>
                    <strong>UPN:</strong> {formData.userPrincipalName}
                  </Typography>
                  <Typography>
                    <strong>Name:</strong> {formData.givenName} {formData.surname}
                  </Typography>
                  <Typography>
                    <strong>Job Title:</strong> {formData.jobTitle || '-'}
                  </Typography>
                  <Typography>
                    <strong>Department:</strong> {formData.department || '-'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Licenses ({formData.selectedLicenses.length})
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  {formData.selectedLicenses.length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {formData.selectedLicenses.map((licenseId) => {
                        const license = availableLicenses.find(
                          (l) => getProp(l, 'skuId') === licenseId
                        );
                        return <Chip key={licenseId} label={getProp(license, 'skuPartNumber')} />;
                      })}
                    </Box>
                  ) : (
                    <Typography color="text.secondary">No licenses selected</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Groups ({formData.selectedGroups.length})
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  {formData.selectedGroups.length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {formData.selectedGroups.map((groupId) => {
                        const group = availableGroups.find((g) => getProp(g, 'id') === groupId);
                        return <Chip key={groupId} label={getProp(group, 'displayName')} />;
                      })}
                    </Box>
                  ) : (
                    <Typography color="text.secondary">No groups selected</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  if (success) {
    return (
      <Box>
        <Box sx={styles.pageHeader}>
          <Box sx={styles.flexCenter}>
            <CheckCircle sx={{ fontSize: 40, mr: 2, color: 'success.main' }} />
            <Typography variant="h4">Onboarding Complete</Typography>
          </Box>
        </Box>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            User has been successfully created!
          </Typography>
          <Typography color="text.secondary" paragraph>
            {formData.displayName} ({formData.userPrincipalName})
          </Typography>
          <Button variant="contained" onClick={() => window.location.reload()}>
            Onboard Another User
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={styles.pageHeader}>
        <Box sx={styles.flexCenter}>
          <PersonAdd sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
          <Typography variant="h4">Employee Onboarding</Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ minHeight: 300, mb: 3 }}>{renderStepContent(activeStep)}</Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button disabled={activeStep === 0 || loading} onClick={handleBack}>
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={loading}
            startIcon={
              loading ? (
                <CircularProgress size={20} />
              ) : activeStep === steps.length - 1 ? (
                <Save />
              ) : null
            }
          >
            {loading ? 'Creating...' : activeStep === steps.length - 1 ? 'Create User' : 'Next'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};
